import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, X } from 'lucide-react';
import { trackCalendlyClick, trackWebCenterDecisionCallClick, CALENDLY_WEBCENTER_URL } from "@/lib/analytics";

const CALENDLY_URL = 'https://outlook.office.com/bookwithme/user/ca2e84f5d95b4280a34073c39ee722f3@ezcloud.ai/meetingtype/cGjhRG1JmkSrBrj2cFWWjA2?anonymous&ismsaljsauthenabled&ep=mlink';
const DISMISS_KEY = 'mobile-sticky-cta-dismissed';
const SCROLL_THRESHOLD_PX = 600;

// The WebCenter/FMW consulting pages funnel to the Decision Call, not the
// product demo. Everything under /solutions/ is consulting except the two
// AP-product vertical pages; the WebCenter tools under /resources/ likewise.
const AP_PRODUCT_SOLUTIONS = new Set([
  '/solutions/healthcare-oracle-fusion',
  '/solutions/education-oracle-ebs',
]);
const isConsultingPath = (path: string): boolean => {
  const p = path.replace(/\/$/, '');
  if (p.startsWith('/solutions/')) return !AP_PRODUCT_SOLUTIONS.has(p);
  return (
    p.startsWith('/resources/webcenter') ||
    p.startsWith('/resources/wcc-') ||
    p.startsWith('/resources/wfr-') ||
    p === '/resources/oracle-fusion-middleware-support-dates'
  );
};

// Floating bottom CTA shown on mobile after the user has scrolled past the
// hero. Dismissable for the session. Hidden on md+ because the sticky nav
// already has a visible "Schedule a Demo" button on desktop.
const MobileStickyCTA = () => {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();
  const consulting = isConsultingPath(pathname);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.sessionStorage.getItem(DISMISS_KEY) === '1') return;

    const onScroll = () => {
      setShow(window.scrollY > SCROLL_THRESHOLD_PX);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleDismiss = () => {
    window.sessionStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  return (
    <div
      aria-hidden={!show}
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pointer-events-none transition-transform duration-300 ${
        show ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="pointer-events-auto mx-auto max-w-md bg-accent text-white rounded-full shadow-2xl flex items-center gap-1 pr-1">
        <a
          href={consulting ? CALENDLY_WEBCENTER_URL : CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-event="mobile-sticky-cta-click"
          onClick={() =>
            consulting
              ? trackWebCenterDecisionCallClick('mobile_sticky')
              : trackCalendlyClick('mobile_sticky')
          }
          className="flex-1 flex items-center justify-center gap-2 font-semibold py-3 pl-5 pr-2"
        >
          <Calendar className="w-4 h-4" aria-hidden />
          {consulting ? 'Book a Decision Call' : 'Book a demo'}
        </a>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss demo button"
          className="p-3 text-white/75 hover:text-white flex-shrink-0"
        >
          <X className="w-4 h-4" aria-hidden />
        </button>
      </div>
    </div>
  );
};

export default MobileStickyCTA;
